// Каркас контентной страницы: «Как это работает», шаблоны, «О сервисе».
//
// Эти страницы заведены ради поиска, но открывает их человек, поэтому у них
// та же оболочка, что у тарифов: знак, бургер-меню с языком, оформлением и
// аккаунтом. Стора и dnd им не нужно — они ничего не считают.
//
// Внизу — ссылки на соседние страницы. Они нужны обоим читателям: роботу,
// который ходит по ссылкам и меню под бургером не видит, и человеку, который
// дочитал до конца и решает, куда дальше.
import type { ReactElement, ReactNode } from 'react';
import { LanguagePicker, useI18n, useT } from '../i18n';
import { AppMenu, AppMenuBlock, AppMenuRow, Logo } from '../ui/shell';
import { PersonalizationPanel } from '../theme';
import { useAuth } from '../auth';
import { useRoute } from '../router';
import { useDocumentMeta } from '../seo';
import { pageContent } from '../content';
import styles from './DocLayout.module.css';

/** Адреса контентных страниц — те же, что знает сервер (server/content.mjs). */
export const DOC_PATHS = {
  how: '/how-it-works',
  templates: '/templates',
  about: '/about',
} as const;

/** Ссылки оболочки: планировщик, три контентные страницы, лайфхаки и тарифы. */
function useNavLinks(): { href: string; label: string }[] {
  const t = useT();
  const { lang } = useI18n();
  const c = pageContent(lang);
  return [
    { href: '/', label: c.nav.home },
    { href: DOC_PATHS.how, label: c.how.title },
    { href: DOC_PATHS.templates, label: c.templates.title },
    // Подпись лайфхаков живёт в словаре интерфейса, а не в `content/pages.json`:
    // своего текста у той страницы нет, его пишут пользователи, и заводить ради
    // одного заголовка запись в контентном файле значило бы держать пустую
    // страницу в документе, который весь про текст.
    { href: '/lifehacks', label: t('lh.title') },
    { href: '/pricing', label: c.nav.pricing },
    { href: DOC_PATHS.about, label: c.about.title },
  ];
}

function DocMenu(): ReactElement {
  const t = useT();
  const { user, signOut, requestSignIn } = useAuth();
  const links = useNavLinks();

  return (
    <AppMenu
      sections={[
        {
          id: 'pages',
          title: t('menu.section.trips'),
          children: (
            <AppMenuBlock>
              {links.map((item) => (
                <a key={item.href} className="btn" href={item.href} data-menu-close>
                  {item.label}
                </a>
              ))}
            </AppMenuBlock>
          ),
        },
        {
          id: 'view',
          title: t('menu.section.view'),
          children: (
            <>
              <AppMenuRow label={t('app.language')}>
                <LanguagePicker />
              </AppMenuRow>
              <AppMenuBlock>
                <PersonalizationPanel />
              </AppMenuBlock>
            </>
          ),
        },
        {
          id: 'account',
          title: t('menu.section.account'),
          children: user ? (
            <>
              <AppMenuRow label={t('account.title')}>
                <span className={styles.menuEmail}>{user.name.trim() || user.email}</span>
              </AppMenuRow>
              <AppMenuBlock>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-menu-close
                  onClick={() => void signOut()}
                >
                  {t('auth.signout')}
                </button>
              </AppMenuBlock>
            </>
          ) : (
            <AppMenuBlock>
              <button
                type="button"
                className="btn btn-primary"
                data-menu-close
                onClick={requestSignIn}
              >
                {t('auth.signin')}
              </button>
            </AppMenuBlock>
          ),
        },
      ]}
    />
  );
}

export interface DocLayoutProps {
  /** Заголовок страницы: он же <h1>, он же заголовок вкладки. */
  title: string;
  /** Мета-описание — то же, что подставляет сервер в <head>. */
  description: string;
  lead: string;
  children: ReactNode;
}

export function DocLayout({ title, description, lead, children }: DocLayoutProps): ReactElement {
  const t = useT();
  const { path } = useRoute();
  const links = useNavLinks();

  useDocumentMeta({ title, description });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <DocMenu />
        <Logo title={t('app.brand')} href="/" />
      </header>

      <article className={styles.doc}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.lead}>{lead}</p>
        {children}
      </article>

      {/* Ссылка на саму себя в подвале не нужна: она никуда не ведёт. Страница
          шаблона считается «своей» для списка шаблонов — из неё туда ведёт
          отдельная ссылка «Все шаблоны» под текстом. */}
      <nav className={styles.footer}>
        {links
          .filter((item) => item.href !== path)
          .map((item) => (
            <a key={item.href} className={styles.link} href={item.href}>
              {item.label}
            </a>
          ))}
      </nav>
    </div>
  );
}
